import { describeRpcError, isAuthorizationError } from "@how/app-kit";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PermissionDenied,
  useFocusWhenReady,
  useReturnFocus,
  DISPLAY_LOCALE,
} from "@how/ui";
import { atCampus, useMyCampusFilter } from "../profile/campus-filter";
import {
  formatPrice,
  formatWhen,
  useCancelAddOns,
  useCancelMyRegistration,
  useMyHousehold,
  usePayForAddOns,
  usePayForRegistration,
  useRegisterMe,
  useUpcomingEvents,
  type MyRegistration,
  type UpcomingOccurrence,
} from "./event-queries";
import { AddOnsModal } from "./AddOnsModal";
import { semanticColor } from "@how/design-system";

function notifyError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? "yellow" : "red",
    title,
    message: described.message,
  });
}

function dayKey(iso: string): string {
  return new Date(iso).toLocaleDateString(DISPLAY_LOCALE, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

/**
 * What is on (ADR 0041, task 8): the next two months by day, filtered by campus and kind, with the way in by each
 * event's rule — register yourself and anyone in your household, or just come. Everything is resolved from the
 * login on the server; the screen sends an occurrence id and the people in the household it was told about.
 */
export function EventsPage() {
  const upcoming = useUpcomingEvents();
  const [campus, setCampus] = useMyCampusFilter();
  const [category, setCategory] = useState<string | null>(null);
  const [registering, setRegistering] = useState<UpcomingOccurrence | null>(null);
  const [cancelling, setCancelling] = useState<{
    occurrence: UpcomingOccurrence;
    mine: MyRegistration;
  } | null>(null);
  const pay = usePayForRegistration();
  const [addingTo, setAddingTo] = useState<{
    occurrence: UpcomingOccurrence;
    mine: MyRegistration;
  } | null>(null);
  // The picker unmounts on close, so focus is handed back to its button by hand (#261).
  const addOnsFocus = useReturnFocus();
  const [params, setParams] = useSearchParams();
  const paid = params.get("paid");
  // Back from Stripe: say what happened once, re-read the list (the webhook has usually landed), and drop the marker.
  useEffect(() => {
    if (paid === null) return;
    notifications.show(
      paid === "yes"
        ? {
            color: "teal",
            title: "Thank you",
            message: "Your payment went through. Your place is confirmed.",
          }
        : {
            color: "gray",
            title: "Not paid",
            message: "Your place is held for a day; you can pay from this page.",
          },
    );
    void upcoming.refetch();
    setParams((current) => {
      current.delete("paid");
      return current;
    });
  }, [paid, setParams, upcoming]);

  const all = (upcoming.data ?? []).filter((o) => !o.isCancelled);
  const listed = all.filter(
    (o) =>
      atCampus(o.event.campus?.name, campus) &&
      (category === null || o.event.category === category),
  );
  const options = (values: (string | null | undefined)[]) =>
    [...new Set(values.filter((v): v is string => typeof v === "string"))]
      .sort()
      .map((v) => ({ value: v, label: v }));
  const days = [...new Set(listed.map((o) => dayKey(o.startsAt)))];

  return (
    <>
      <PageHeader
        title="Events"
        description="What is on in the next two months. Register where an event asks you to; otherwise just come."
      />
      {upcoming.isPending ? (
        <LoadingState rows={6} />
      ) : upcoming.isError ? (
        isAuthorizationError(upcoming.error) ||
        describeRpcError(upcoming.error).kind === "denied" ? (
          <PermissionDenied />
        ) : (
          <ErrorState
            message="Could not load what is on."
            onRetry={() => {
              void upcoming.refetch();
            }}
          />
        )
      ) : (
        <Stack gap="lg">
          <Group gap="sm" wrap="wrap">
            <Select
              aria-label="Campus"
              placeholder="Any campus"
              clearable
              data={options(all.map((o) => o.event.campus?.name))}
              value={campus}
              onChange={setCampus}
              w={200}
            />
            <Select
              aria-label="Kind"
              placeholder="Any kind"
              clearable
              data={options(all.map((o) => o.event.category))}
              value={category}
              onChange={setCategory}
              w={190}
            />
          </Group>
          {listed.length === 0 ? (
            <EmptyState
              title={all.length === 0 ? "Nothing on yet" : "Nothing matches"}
              description={
                all.length === 0
                  ? "Your church has not listed anything for the next two months."
                  : "Try fewer choices."
              }
            />
          ) : (
            days.map((day) => (
              <section key={day}>
                <Title order={2} size="h4" mb="xs">
                  {day}
                </Title>
                <Stack gap="sm">
                  {listed
                    .filter((o) => dayKey(o.startsAt) === day)
                    .map((o) => (
                      <OccurrenceCard
                        key={o.occurrenceId}
                        occurrence={o}
                        onRegister={() => {
                          setRegistering(o);
                        }}
                        onCancel={(mine) => {
                          setCancelling({ occurrence: o, mine });
                        }}
                        onPay={(mine) => {
                          pay.mutate(mine.registrationId, {
                            onError: (error) => {
                              notifyError("Could not start the payment", error);
                            },
                          });
                        }}
                        onAddOns={(mine) => {
                          addOnsFocus.remember();
                          setAddingTo({ occurrence: o, mine });
                        }}
                      />
                    ))}
                </Stack>
              </section>
            ))
          )}
        </Stack>
      )}

      <RegisterModal
        occurrence={registering}
        onClose={() => {
          setRegistering(null);
        }}
      />
      <CancelModal
        target={cancelling}
        onClose={() => {
          setCancelling(null);
        }}
      />
      {addingTo === null ? null : (
        <AddOnsModal
          occurrence={addingTo.occurrence}
          registration={addingTo.mine}
          onClose={() => {
            setAddingTo(null);
            addOnsFocus.restore();
          }}
        />
      )}
    </>
  );
}

function OccurrenceCard({
  occurrence: o,
  onRegister,
  onCancel,
  onPay,
  onAddOns,
}: {
  occurrence: UpcomingOccurrence;
  onRegister: () => void;
  onCancel: (mine: MyRegistration) => void;
  onPay: (mine: MyRegistration) => void;
  onAddOns: (mine: MyRegistration) => void;
}) {
  const e = o.event;
  const full = e.capacity !== null && o.registered >= e.capacity;
  const time = new Date(o.startsAt).toLocaleTimeString(DISPLAY_LOCALE, {
    hour: "numeric",
    minute: "2-digit",
    ...(e.campus?.timeZone ? { timeZone: e.campus.timeZone } : {}),
  });
  // Every card has the same buttons; each names its event and date for a screen reader (issue #285).
  const which = `${e.title}, ${formatWhen(o.startsAt, e.campus?.timeZone)}`;
  const registerLabel = full
    ? "Join the waiting list"
    : o.mine.length > 0
      ? "Register someone else"
      : "Register";
  return (
    <Card data-occurrence={o.occurrenceId}>
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Text fw={600}>{e.title}</Text>
            <Text size="sm">
              {time}
              {e.location === null ? "" : ` · ${e.location}`}
              {e.campus === null ? "" : ` · ${e.campus.name}`}
            </Text>
          </div>
          {e.registration === "open" ? (
            full ? (
              <Badge variant="light" color="gray">
                Full
              </Badge>
            ) : (
              <Badge variant="light" color="teal">
                Register
              </Badge>
            )
          ) : null}
        </Group>
        {e.description === null ? null : (
          <Text size="sm" c="dimmed">
            {e.description}
          </Text>
        )}
        {o.note === null ? null : (
          <Text size="sm" fs="italic">
            {o.note}
          </Text>
        )}
        {o.mine.length > 0 ? (
          <Group gap="xs" wrap="wrap">
            {o.mine.map((m) => (
              <Badge
                key={m.registrationId}
                variant="light"
                color={m.status === "waitlisted" ? "yellow" : "teal"}
                rightSection={
                  m.status === "attended" ? null : (
                    <Button
                      size="compact-xs"
                      variant="transparent"
                      color="gray"
                      data-registration={m.registrationId}
                      aria-label={`${
                        m.isMe ? "Cancel your registration" : `Cancel ${m.name}'s registration`
                      } for ${which}`}
                      onClick={() => {
                        onCancel(m);
                      }}
                    >
                      ×
                    </Button>
                  )
                }
              >
                {m.isMe ? "You" : m.name}
                {m.status === "waitlisted"
                  ? " (waiting list)"
                  : m.status === "attended"
                    ? " (came)"
                    : m.paymentStatus === "paid"
                      ? " (paid)"
                      : m.paymentStatus === "pending"
                        ? " (unpaid)"
                        : m.paymentStatus === "refunded"
                          ? " (refunded)"
                          : ""}
              </Badge>
            ))}
          </Group>
        ) : null}
        {o.mine.some((m) => m.paymentStatus === "pending" && m.status !== "cancelled") ? (
          <Group gap="xs">
            {o.mine
              .filter((m) => m.paymentStatus === "pending" && m.status !== "cancelled")
              .map((m) => (
                <Button
                  key={m.registrationId}
                  size="compact-sm"
                  aria-label={`Pay ${
                    e.priceCents === null
                      ? ""
                      : `${formatPrice(e.priceCents + pendingAddOnCents(m), e.currency)} `
                  }${m.isMe ? "" : `for ${m.name} `}for ${which}`}
                  onClick={() => {
                    onPay(m);
                  }}
                >
                  Pay{" "}
                  {e.priceCents === null
                    ? ""
                    : formatPrice(e.priceCents + pendingAddOnCents(m), e.currency)}
                  {m.isMe ? "" : ` for ${m.name}`}
                </Button>
              ))}
            <Text size="xs" c="dimmed">
              Your place is held for a day while you pay.
            </Text>
          </Group>
        ) : null}
        <AddOnsLines occurrence={o} onAddOns={onAddOns} />
        <Group justify="space-between" align="center" mt="xs">
          <Text size="xs" c="dimmed">
            {e.priceCents !== null && e.registration === "open"
              ? `${formatPrice(e.priceCents, e.currency)} per person · `
              : ""}
            {e.registration === "none"
              ? "Just come"
              : e.registration === "closed"
                ? "Registration has closed"
                : e.capacity === null
                  ? `${String(o.registered)} registered`
                  : `${String(o.registered)} of ${String(e.capacity)} places taken`}
          </Text>
          {e.registration === "open" ? (
            <Button
              size="compact-sm"
              variant={full ? "subtle" : "light"}
              data-register
              aria-label={`${registerLabel} for ${which}`}
              onClick={onRegister}
            >
              {registerLabel}
            </Button>
          ) : null}
        </Group>
      </Stack>
    </Card>
  );
}

/** Register yourself and anyone in your household: each person is their own registration (ADR 0041). */
function RegisterModal({
  occurrence,
  onClose,
}: {
  occurrence: UpcomingOccurrence | null;
  onClose: () => void;
}) {
  const household = useMyHousehold();
  const register = useRegisterMe();
  const [chosen, setChosen] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const already = new Set(
    (occurrence?.mine ?? []).filter((m) => m.status !== "cancelled").map((m) => m.personId),
  );
  const people = (household.data ?? []).filter((p) => !already.has(p.personId));
  return (
    <Modal
      opened={occurrence !== null}
      onClose={onClose}
      title={occurrence === null ? "" : `Register for ${occurrence.event.title}`}
      radius="lg"
    >
      <Stack gap="sm">
        {occurrence === null ? null : (
          <Text size="sm" c="dimmed">
            {formatWhen(occurrence.startsAt, occurrence.event.campus?.timeZone)}
            {occurrence.event.location === null ? "" : ` · ${occurrence.event.location}`}
          </Text>
        )}
        {household.isPending ? (
          <LoadingState rows={2} />
        ) : people.length === 0 ? (
          <Text size="sm" c="dimmed">
            Everyone in your household is already registered for this date.
          </Text>
        ) : (
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Who is coming
            </Text>
            {people.map((p) => (
              <Checkbox
                key={p.personId}
                label={`${p.isMe ? "Me" : p.name}${p.role === "child" ? " (child)" : ""}`}
                checked={chosen.includes(p.personId)}
                onChange={(event) => {
                  const on = event.currentTarget.checked;
                  setChosen((c) => (on ? [...c, p.personId] : c.filter((id) => id !== p.personId)));
                }}
              />
            ))}
          </Stack>
        )}
        <Textarea
          label="Anything the organisers should know"
          placeholder="Wheelchair access, allergies, a lift needed"
          value={note}
          maxLength={300}
          autosize
          minRows={1}
          onChange={(event) => {
            setNote(event.currentTarget.value);
          }}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={chosen.length === 0}
            loading={register.isPending}
            onClick={() => {
              if (occurrence === null) return;
              register.mutate(
                { occurrenceId: occurrence.occurrenceId, personIds: chosen, note },
                {
                  onSuccess: () => {
                    setChosen([]);
                    setNote("");
                    onClose();
                    notifications.show({
                      color: semanticColor("success"),
                      title: "Registered",
                      message: `You are down for ${occurrence.event.title}. If a date is full you are on the waiting list, and it says so.`,
                    });
                  },
                  onError: (error) => {
                    notifyError("Could not register", error);
                  },
                },
              );
            }}
          >
            Register
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CancelModal({
  target,
  onClose,
}: {
  target: { occurrence: UpcomingOccurrence; mine: MyRegistration } | null;
  onClose: () => void;
}) {
  const cancel = useCancelMyRegistration();
  const focusWhenReady = useFocusWhenReady();
  return (
    <ConfirmActionModal
      opened={target !== null}
      onClose={onClose}
      title="Cancel this registration"
      message={
        target === null
          ? ""
          : `Cancel ${target.mine.isMe ? "your" : `${target.mine.name}'s`} registration for ${target.occurrence.event.title} on ${formatWhen(target.occurrence.startsAt, target.occurrence.event.campus?.timeZone)}? You can register again later if there is room.`
      }
      confirmLabel="Cancel registration"
      danger
      loading={cancel.isPending}
      onConfirm={() => {
        if (target === null) return;
        const { occurrence, mine } = target;
        cancel.mutate(mine.registrationId, {
          onSuccess: () => {
            // The registration and its cancel button leave the card; focus goes to its Register (issue #285).
            focusWhenReady(`[data-occurrence="${occurrence.occurrenceId}"] [data-register]`, {
              gone: `[data-registration="${mine.registrationId}"]`,
            });
            onClose();
          },
          onError: (error) => {
            notifyError("Could not cancel", error);
          },
        });
      }}
    />
  );
}

/** The add-ons not paid yet that ride with a place's payment. */
function pendingAddOnCents(m: MyRegistration): number {
  return m.addOns
    .filter((a) => a.status === "pending_payment")
    .reduce((sum, a) => sum + a.totalCents, 0);
}

/**
 * Each of my registrations' add-ons (ADR 0047 step 3): what was chosen and whether it is paid, with Pay or Remove for
 * ones not paid yet, and "Add-ons…" while the event offers some and none are waiting.
 */
function AddOnsLines({
  occurrence: o,
  onAddOns,
}: {
  occurrence: UpcomingOccurrence;
  onAddOns: (mine: MyRegistration) => void;
}) {
  const pay = usePayForAddOns();
  const remove = useCancelAddOns();
  const focusWhenReady = useFocusWhenReady();
  const rows = o.mine.filter(
    (m) =>
      m.status !== "cancelled" &&
      (m.addOns.length > 0 || (o.event.addOns > 0 && m.status !== "attended")),
  );
  if (rows.length === 0) return null;
  return (
    <Stack gap={4}>
      {rows.map((m) => {
        const waiting = m.addOns.find((a) => a.status === "pending_payment");
        return (
          <Stack key={m.registrationId} gap={2}>
            {m.addOns.map((a) => (
              <Group key={a.orderId} gap="xs" wrap="wrap">
                <Text size="sm">
                  Add-ons{m.isMe ? "" : ` for ${m.name}`}: {a.items ?? ""} ·{" "}
                  {formatPrice(a.totalCents, o.event.currency)}{" "}
                  {a.status === "pending_payment"
                    ? m.paymentStatus === "pending"
                      ? "(paid with the place)"
                      : "(unpaid)"
                    : a.status === "fulfilled"
                      ? "(handed over)"
                      : "(paid)"}
                </Text>
                {a.status === "pending_payment" && m.paymentStatus !== "pending" ? (
                  <Button
                    size="compact-xs"
                    loading={pay.isPending}
                    aria-label={`Pay ${formatPrice(a.totalCents, o.event.currency)} for the add-ons${m.isMe ? "" : ` for ${m.name}`}, ${o.event.title}`}
                    onClick={() => {
                      pay.mutate(a.orderId, {
                        onError: (error) => {
                          notifyError("Could not open the payment", error);
                        },
                      });
                    }}
                  >
                    Pay {formatPrice(a.totalCents, o.event.currency)}
                  </Button>
                ) : null}
                {a.status === "pending_payment" ? (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    loading={remove.isPending}
                    data-addon-order={a.orderId}
                    aria-label={`Remove the unpaid add-ons${m.isMe ? "" : ` for ${m.name}`}, ${o.event.title}`}
                    onClick={() => {
                      remove.mutate(a.orderId, {
                        onSuccess: () => {
                          // The line goes; its registration's Add-ons button comes back (issue #285).
                          focusWhenReady(`[data-addons-for="${m.registrationId}"]`, {
                            gone: `[data-addon-order="${a.orderId}"]`,
                          });
                        },
                        onError: (error) => {
                          notifyError("Could not remove the add-ons", error);
                        },
                      });
                    }}
                  >
                    Remove
                  </Button>
                ) : null}
              </Group>
            ))}
            {o.event.addOns > 0 && waiting === undefined && m.status !== "attended" ? (
              <Group>
                <Button
                  size="compact-xs"
                  variant="light"
                  data-addons-for={m.registrationId}
                  aria-label={`${m.addOns.length === 0 ? "Add-ons" : "More add-ons"}${m.isMe ? "" : ` for ${m.name}`}, ${o.event.title}`}
                  onClick={() => {
                    onAddOns(m);
                  }}
                >
                  {m.addOns.length === 0 ? "Add-ons" : "More add-ons"}
                  {m.isMe ? "" : ` for ${m.name}`}
                </Button>
              </Group>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}
